import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import chatArchiveService from "../../../services/chatArchiveService";
import { ArchievedChats } from "../../../types/chatActions/archive";
interface InitialState {
  isLoading: boolean;
  isError: boolean;
  data: ArchievedChats[];
  total: number;
  isFetchingMore: boolean; // Optional property to track if more data is being fetched
  hasFetchedAll: boolean; // Optional property to track if all data has been fetched
}
const initialState: InitialState = {
  isLoading: false,
  isError: false,
  data: [],
  total: 0,
  isFetchingMore: false, // Initialize to false
  hasFetchedAll: false, // Optional property to track if all data has been fetched
};

export const getAllArchivedChats = createAsyncThunk(
  "archivedChats/getAllArchivedChats",
  async (args: { page: number }, thunkAPI) => {
    try {
      const { page = 1 } = args;
      const response = await chatArchiveService.getAlltArchivedSessions({
        page: Number(page),
        limit: 10,
      });
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const unArchiveChat = createAsyncThunk(
  "archivedChats/unArchiveChat",
  async (sessionId: string, thunkAPI) => {
    try {
      const response = await chatArchiveService.unArchiveChat(sessionId, 1);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const unArchiveAllChats = createAsyncThunk(
  "archivedChats/unArchiveAllChats",
  async (_, thunkAPI) => {
    try {
      const response = await chatArchiveService.unArchiveAllSessions();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
const archievedChatsReducer = createSlice({
  name: "archivedChats",
  initialState,
  reducers: {
    removeFromArchive(state, action) {
      const sessionId = action.payload;
      // Remove the chat with the given sessionId from the data array
      state.data = state.data.filter((chat) => chat.chat_id !== sessionId);
      state.total -= 1; // Decrease total count
    },
    addToArchive(state, action) {
      const newChat = action.payload;
      // Add the new chat to the data array
      state.data.unshift(newChat); // Add to the beginning of the array
      state.total += 1; // Increase total count
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllArchivedChats.pending, (state, action) => {
        console.log("Fetching archived chats", action.meta.arg);
        if (action.meta.arg.page === 1) {
          state.data = []; // Reset data when fetching the first page
          state.isLoading = true;
          state.hasFetchedAll = false; // Reset hasFetchedAll when fetching the first page
        }
        if (action.meta.arg.page > 1) {
          state.isFetchingMore = true; // Set to true when fetching more data
        }
      })
      .addCase(getAllArchivedChats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        if (action.meta.arg.page === 1 && action.payload.data?.data === null) {
          state.data = [];
          state.total = 0; // Reset data and total if no chats found
        } else {
          state.isFetchingMore = false; // Reset fetching more state
          if (action.payload.data.data) {
            state.data.push(...action.payload.data.data); // Append new data to existing data
            state.total = action.payload.data.total; // Update total count
          } else {
            state.hasFetchedAll = true; // Set to true if no more data is available
          }
        }
      })
      .addCase(getAllArchivedChats.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
      });
    builder
      .addCase(unArchiveAllChats.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(unArchiveAllChats.fulfilled, (state) => {
        state.isLoading = false;
        state.isError = false;
        state.data = [];
        state.total = 0; // Reset data and total after unarchiving
      })
      .addCase(unArchiveAllChats.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
      });
    builder
      .addCase(unArchiveChat.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(unArchiveChat.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;

        state.data = state.data.filter(
          (chat) => chat.chat_id !== action.payload.sessionId
        );
        state.total -= 1; // Decrease total count
      })
      .addCase(unArchiveChat.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
      });
  },
});

export const { removeFromArchive, addToArchive } =
  archievedChatsReducer.actions;

export default archievedChatsReducer.reducer;
